<template>
  <settings-modal
    :locked="isLoading"
    class="bl-ob-application-editor-modal t-ob-application-editor-modal"
  >
    <span
      slot="heading"
      class="font-weight-bold"
    >
      <template v-if="isFormEditable">{{ isNewApplication ? 'Add' : 'Edit' }} application</template>
      <template v-else>View application</template>
    </span>

    <v-form
      autocomplete="off"
      autocorrect="off"
      spellcheck="false"
    >
      <v-layout row>
        <v-flex>
          <v-select
            v-model="form.branding"
            :items="brandingOptions"
            item-text="name"
            item-value="value"
            label="Branding"
            class="t-branding-field"
          />
        </v-flex>
      </v-layout>

      <v-layout
        v-if="parsedFormInput.branding === BRANDING_CUSTOM"
        row
      >
        <v-flex>
          <v-text-field
            label="Logo image"
            :value="form.logo"
            :loading="uploadLogoApiRequest.loading"
            :disabled="uploadLogoApiRequest.loading || isLoading"
            :error-messages="logoUploadInputErrorMessage"
            :hint="`Required field. Preferred image size: 360 x 360 pixels. Types: ${logoFileTypes.join(', ')}`"
            class="t-logo-upload-field"
            readonly
            clearable
            persistent-hint
            @click:clear="onLogoUploadInputClear"
            @click="onLogoUploadInputClick"
          />
          <input
            ref="fileInput"
            type="file"
            :accept="logoFileTypes"
            class="t-logo-upload-input"
            style="display: none"
            @change="onLogoFileSelect"
          >
        </v-flex>

        <v-flex v-if="form.logo">
          <div class="bl-ob-application-editor-modal__logo-wrapper">
            <div
              class="bl-ob-application-editor-modal__logo-image"
              :style="`background-image: url('${logoUrl}')`"
            />
          </div>
        </v-flex>
      </v-layout>

      <v-layout row>
        <v-flex>
          <v-text-field
            slot="activator"
            v-model="form.name"
            label="Name"
            :readonly="!isFormEditable"
            :disabled="isLoading"
            :error-messages="getValidationErrorMessage('name')"
            class="t-name-field"
            hint="Required field"
            persistent-hint
          />
        </v-flex>
      </v-layout>

      <v-layout row>
        <v-flex>
          <v-autocomplete
            v-model="form.merchantCategoryCode"
            :items="merchantCategoryCodeOptions"
            item-text="name"
            item-value="value"
            label="Merchant Category Code"
            :disabled="isLoading"
            :readonly="!isFormEditable"
            :error-messages="getValidationErrorMessage('merchantCategoryCode')"
            class="t-merchant-category-code-field"
            hint="Required field"
            persistent-hint
          />
        </v-flex>
      </v-layout>

      <v-layout row>
        <v-flex>
          <v-autocomplete
            v-model="form.ppcUserGroup"
            :items="ppcUserGroupOptions"
            label="PPC User Group"
            :disabled="isLoading"
            :readonly="!isFormEditable"
            :error-messages="getValidationErrorMessage('ppcUserGroup')"
            class="t-ppc-user-group-field"
            hint="Required field"
            persistent-hint
          />
        </v-flex>
      </v-layout>

      <v-layout row>
        <v-flex>
          <v-text-field
            slot="activator"
            v-model="form.accountNumber"
            label="IBAN"
            :disabled="isLoading"
            :readonly="!isFormEditable"
            :error-messages="getValidationErrorMessage('accountNumber')"
            class="t-account-number-field"
            hint="Required field"
            persistent-hint
          />
        </v-flex>
      </v-layout>

      <v-layout row>
        <v-flex>
          <v-autocomplete
            v-model="form.restrictedBankCountries"
            :items="countryOptions"
            item-text="name"
            item-value="value"
            label="Restricted bank countries"
            multiple
            :disabled="isLoading"
            :readonly="!isFormEditable"
            :error-messages="getValidationErrorMessage('restrictedBankCountries')"
            class="t-restricted-bank-countries-field"
          />
        </v-flex>
      </v-layout>

      <v-layout row>
        <v-flex>
          <v-autocomplete
            v-model="form.restrictedSenderCountries"
            :items="countryOptions"
            item-text="name"
            item-value="value"
            label="Restricted sender countries"
            multiple
            :disabled="isLoading"
            :readonly="!isFormEditable"
            :error-messages="getValidationErrorMessage('restrictedSenderCountries')"
            class="t-restricted-sender-countries-field"
          />
        </v-flex>
      </v-layout>

      <v-layout row>
        <v-flex>
          <v-autocomplete
            v-model="form.hostedPagesCountries"
            :items="hostedPagesOptions"
            item-text="name"
            item-value="value"
            label="Hosted pages countries"
            multiple
            :disabled="isLoading"
            :readonly="!isFormEditable"
            :error-messages="getValidationErrorMessage('hostedPagesCountries')"
            class="t-hosted-pages-countries-field"
          />
        </v-flex>
      </v-layout>

      <v-layout row>
        <v-flex>
          <v-textarea
            v-model="form.restrictedIbans"
            label="Restricted IBANs"
            no-resize
            hint="Use new line as separator"
            persistent-hint
            :disabled="isLoading"
            :readonly="!isFormEditable"
            :error-messages="getValidationErrorMessage('restrictedIbans')"
            class="t-restricted-ibans-field"
          />
        </v-flex>
      </v-layout>
    </v-form>

    <error-alert
      :error="error"
      class="t-error-message mt-4"
    />

    <template slot="actions">
      <v-btn
        v-if="isFormEditable"
        class="t-save-button"
        color="primary"
        medium
        flat
        :disabled="!canSubmitForm"
        @click="onSaveButtonClick"
      >Save</v-btn>

      <v-btn
        class="t-cancel-button"
        color="primary"
        medium
        flat
        :disabled="isLoading"
        @click="onCloseButtonClick"
      >{{ isFormEditable ? 'cancel' : 'close' }}</v-btn>
    </template>
  </settings-modal>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import modal from '@/mixins/modal'
  import SettingsModal from '@/components/modals/SettingsModal'
  import form from '@/mixins/form'
  import countries from '@/mixins/countries'
  import ErrorAlert from '@/components/ui/ErrorAlert'
  import OpenBankingApi from '@/services/Api/OpenBankingApi'
  import ApiRequest from '@/services/ApiRequests/ApiRequest'
  import flashMessages from '@/mixins/flashMessages'
  import { ROLES } from '@/store/admin'
  import OpenBankingApp from '@/models/OpenBankingApp'
  import { AppConfig } from '@/config/config'

  const BRANDING_NONE = 'NONE'
  const BRANDING_CUSTOM = 'CUSTOM'
  const BRANDING_DEFAULT = 'DEFAULT'

  const BRANDING = {
    [BRANDING_DEFAULT]: 'Default',
    [BRANDING_NONE]: 'None',
    [BRANDING_CUSTOM]: 'Custom'
  }

  const DEFAULT_LOGO = 'bilder.svg'

  export default {
    name: 'OpenBankingAppEditorModal',
    components: {
      ErrorAlert,
      SettingsModal
    },
    mixins: [form, modal, countries, flashMessages],
    data () {
      return {
        BRANDING_CUSTOM,
        logoFileTypes: ['.jpg', '.jpeg', '.png', '.svg'],
        error: undefined,
        isLoading: false,
        isUploading: false,
        logoUploadError: null,
        uploadLogoApiRequest: new ApiRequest(OpenBankingApi.uploadLogo),
        createApplicationApiRequest: new ApiRequest(OpenBankingApi.createApplication),
        updateApplicationApiRequest: new ApiRequest(OpenBankingApi.updateApplication),
        form: {
          name: undefined,
          logo: null,
          branding: undefined,
          accountNumber: undefined,
          restrictedIbans: undefined,
          restrictedBankCountries: [],
          restrictedSenderCountries: [],
          hostedPagesCountries: [],
          merchantCategoryCode: undefined,
          ppcUserGroup: undefined
        }
      }
    },
    computed: {
      ...mapGetters('modal', ['modal']),
      ...mapGetters('admin', ['hasRole']),
      profile () {
        return this.modal.data.profile
      },
      application () {
        return this.modal.data.application
      },
      isNewApplication () {
        return this.application?.id == null
      },
      isFormEditable () {
        return this.hasRole([ROLES.SUPPORT_WRITE])
      },
      successCallback () {
        return this.modal.data.successCallback
      },
      requiredFields () {
        const requiredFields = [
          'name',
          'accountNumber',
          'merchantCategoryCode',
          'ppcUserGroup'
        ]

        return this.parsedFormInput.branding === BRANDING_CUSTOM
          ? [...requiredFields, 'logo']
          : requiredFields
      },
      brandingOptions () {
        return Object.entries(BRANDING).map(([value, name]) => ({
          name,
          value
        }))
      },
      countryOptions () {
        return this.allowedCountries.map((code) => ({
          name: this.getCountryName(code),
          value: code
        }))
      },
      hostedPagesOptions () {
        return this.allowedCountries
          .filter((code) => (
            AppConfig.YAPILY_HOSTED_PAGES_COUNTRIES.includes(code)
          ))
          .map((code) => ({
            name: this.getCountryName(code),
            value: code
          }))
      },
      logoUrl () {
        if (this.form.logo != null) {
          return `${AppConfig.STORAGE_SERVICE_URL}/open-banking/${this.form.logo}`
        }
      },
      logoUploadInputErrorMessage () {
        if (this.getValidationErrorMessage('imageName') != null) {
          return this.getValidationErrorMessage('imageName')
        }

        if (this.logoUploadError == null) {
          return
        }

        const systemError = this.logoUploadError.findFirstSystemError()

        if (systemError != null) {
          return systemError.message
        }
      },
      canSubmitForm () {
        return this.isFormFilled && !this.isLoading && !this.uploadLogoApiRequest.loading
      },
      merchantCategoryCodeOptions () {
        return OpenBankingApp.merchantCategoryCodeOptions
      },
      ppcUserGroupOptions () {
        return OpenBankingApp.ppcUserGroupOptions
      }
    },
    created () {
      if (this.isNewApplication) {
        return
      }

      const branding = this.application.logo === DEFAULT_LOGO
        ? BRANDING_DEFAULT
        : this.application.logo == null
          ? BRANDING_NONE
          : BRANDING_CUSTOM

      this.setFormData({
        branding,
        logo: branding === BRANDING_CUSTOM ? this.application.logo : null,
        name: this.application.name,
        accountNumber: this.application.accountNumber,
        restrictedIbans: this.application.restrictedIbans?.join('\r\n'),
        restrictedBankCountries: this.application.restrictedBankCountries,
        restrictedSenderCountries: this.application.restrictedSenderCountries,
        hostedPagesCountries: this.application.hostedPagesCountries,
        merchantCategoryCode: this.application.merchantCategoryCode,
        ppcUserGroup: this.application.ppcUserGroup
      })
    },
    methods: {
      ...mapActions('modal', ['closeModal']),
      onSaveButtonClick () {
        this.setError(null)
        this.setLogoUploadError(null)

        this.setIsLoading(true)

        const requestData = this.formToApiRequest(this.parsedFormInput)
        const apiRequest = this.isNewApplication
          ? this.createApplicationApiRequest.execute(requestData)
          : this.updateApplicationApiRequest.execute(this.application.id, requestData)

        apiRequest
          .then(() => {
            this.successCallback()
            this.showSuccessFlashMessage('Application successfully saved!')
          })
          .catch((error) => this.setError(error))
          .finally(() => this.setIsLoading(false))
      },
      onCloseButtonClick () {
        this.closeModal()
      },
      onLogoFileSelect () {
        const formData = new FormData()
        formData.append('file', this.$refs.fileInput.files[0])

        this.setFieldValue('logo', null)
        this.clearLogoFileInput()

        this.uploadLogoApiRequest.execute(formData)
          .then(({ fileName }) => this.setFieldValue('logo', fileName))
          .catch(this.setLogoUploadError)
      },
      onLogoUploadInputClear () {
        this.setLogoUploadError(null)
        this.setFieldValue('logo', null)
      },
      onLogoUploadInputClick () {
        if (this.parsedFormInput.imageName == null) {
          this.setLogoUploadError(null)
          this.openLogoFileSelection()
        }
      },
      formToApiRequest (form) {
        const restrictedIbans = form.restrictedIbans
          ?.split(/\r?\n/)
          ?.filter((item) => item.trim() !== '') ?? []

        return {
          ...this.isNewApplication ? {
            country: this.profile.country,
            city: this.profile.city,
            address: this.profile.address,
            profileId: this.profile.id,
            profileName: this.profile.name,
            profileOnboarderId: this.profile.onboarderId,
            postCode: this.profile.zip
          } : {},
          logo: form.branding === BRANDING_DEFAULT
            ? DEFAULT_LOGO
            : form.branding === BRANDING_CUSTOM
              ? form.logo
              : null,
          name: form.name,
          accountNumber: form.accountNumber,
          restrictedBankCountries: form.restrictedBankCountries,
          restrictedSenderCountries: form.restrictedSenderCountries,
          hostedPagesCountries: form.hostedPagesCountries,
          merchantCategoryCode: form.merchantCategoryCode,
          ppcUserGroup: form.ppcUserGroup,
          restrictedIbans
        }
      },
      openLogoFileSelection () {
        this.$refs.fileInput.click()
      },
      clearLogoFileInput () {
        this.$refs.fileInput.value = null
      },
      setLogoUploadError (logoUploadError) {
        this.$set(this, 'logoUploadError', logoUploadError)
      },
      setIsLoading (isLoading) {
        this.$set(this, 'isLoading', isLoading)
      }
    }
  }
</script>

<style lang="scss">
  .bl-ob-application-editor-modal {
    &__logo-wrapper {
      border-left: 1px solid #9e9e9e;
      padding-left: 10px;
    }

    &__logo-image {
      width: 100px;
      height: 84px;
      background-size: contain;
      background-position: center center;
    }
  }
</style>
